/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {DEFAULT_HALL, EMAIL_VERIFICATION_REQUIRED} from "helpers/config";
import {HallCode} from "types/enums/HallCode";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useTypedSelector} from "helpers/reducers";
import {countryList, filteredCountryList, PhoneCountry} from "utils/countryUtils";
import {useForm} from "react-hook-form";
import {CompanyInputs} from "types/entities/specific/CompanyInputs";
import clsx from "clsx";
import CardLabel from "pages/Front/Booking/components/BookingCustomer/CardLabel";
import {useRequiredField} from "utils/bookingUtils";
import CompanyFormInput from "pages/Front/Booking/components/CompanyForm/CompanyFormInput";
import CompanyFormPhoneWithCountrySelect
    from "pages/Front/Booking/components/CompanyForm/CompanyFormPhoneWithCountrySelect";
import {yupResolver} from "@hookform/resolvers/yup";
import {ArboSellerAddressMandatorySchemaBasicRequired} from "pages/Front/Booking/Schemas/arboAddressSchema";
import {CustomerType} from "pages/Front/Booking/components/BookingCheckout/BookingCheckoutForm";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import {toast} from "react-toastify";
import {Company} from "types/entities/Company";
import {useDispatch} from "useDispatch";
import {ThunkAction} from "model/store/thunk";
import BookingAction from "store/booking/BookingAction";
import CompanyFormDropdown, {
    CompanyFormDropdownOption,
} from "pages/Front/Booking/components/CompanyForm/CompanyFormDropdown";
import {CheckIcon} from "@heroicons/react/24/solid";
import {FireIcon} from "@heroicons/react/24/outline";
import {consoleLog} from "utils/utility";
import SellerAction from "store/seller/SellerAction";

interface SellerCompanyFormProps {
    onSubmitted: (company: Company) => void;
    company?: Company;
    editEnabled: boolean;
    hideReservationNotes?: boolean;
}

const SellerCompanyForm = (props: SellerCompanyFormProps) => {
    const {t} = useTranslation();
    const hall = useTypedSelector(state => state.hallState.hall);
    const dispatch = useDispatch();
    const packagesCart = useTypedSelector(state => state.bookingState.bookingProcess.packageCart);
    const emailVerified = EMAIL_VERIFICATION_REQUIRED ? useTypedSelector(state => state.bookingState.bookingProcess.emailVerified) : true;
    const companyTypes = [
        {id: "private", type: CustomerType.Private},
        {id: "business", type: CustomerType.Business},
    ];
    const businessCategories: CompanyFormDropdownOption[] = [
        {id: "AutomobilUndMobilitat", name: t("Automobilität")},
        {id: "Dienstleistungen", name: t("Dienstleistungen")},
        {id: "FinanzwesenAndVersicherungen", name: t("Finanzwesen & Versicherungen")},
        {id: "FreizeitAndUnterhaltung", name: t("Freizeit & Unterhaltung")},
        {id: "GastronomieTourismusUndHotellerie", name: t("Gastronomie, Tourismus und Hotellerie")},
        {id: "GewerbeAndBauwesen", name: t("Gewerbe & Bauwesen")},
        {id: "GesundheitAndPflege", name: t("Gesundheit & Pflege")},
        {id: "GutertransportAndLogistik", name: t("Gütertransport & Logistik")},
        {id: "HandelAndDetailhandel", name: t("Handel & Detailhandel")},
        {id: "Immobilienbranche", name: t("Immobilienbranche")},
        {id: "IndustrieAndProduktion", name: t("Industrie & Produktion")},
        {id: "OffentlicherSektor", name: t("Öffentlicher Sektor")},
        {id: "PersonenmobilitatAndVerkehr", name: t("Personenmobilität & Verkehr")},
        {id: "PharmaAndMedtech", name: t("Pharma & Medtech")},
        {id: "PrivateEducationAndUniversitaten", name: t("Private Education & Universitäten")},
        {id: "PrivateGeburtstagePolterabende", name: t("Private, Geburtstage und Polterabende")},
        {id: "SchulenBildungAndSoziales", name: t("Schulen, öffentliche Bildung & Soziales")},
        {id: "SportAndVereine", name: t("Sport & Vereine")},
        {id: "TechnologieAndIT", name: t("Technologie & IT")},
    ];

    const defaultPhoneCountryTextCode = "CH";
    const defaultCountry = countryList.find(item => item.textCode === defaultPhoneCountryTextCode);
    const defaultPhoneCountry: PhoneCountry = defaultCountry ? defaultCountry : countryList[161];
    const hideReservationNotes = !props?.hideReservationNotes ? false : props.hideReservationNotes;
    const [selectedCustomerType, setSelectedCustomerType] = useState<CustomerType>(props.company?.name !== "" ? CustomerType.Business : CustomerType.Private);
    const [selectedCountry, setSelectedCountry] = useState<PhoneCountry>(defaultPhoneCountry ? defaultPhoneCountry : countryList[0]);
    const defaultValues = props.company
        ? {
            ...(props.company || {}),
            // is_newsletter_enabled: true,
            company_name: (props.company && props.company.name) || "",
            company_type: false,
            business_category: props.company.business_category || "",
        }
        : {
            ...{},
            // is_newsletter_enabled: true,
            company_type: false,
            // tos: false,
            phone: "",
            company_name: "",
            business_category: "",
        };
    // const schema = ArboSellerAddressMandatorySchema;
    const schema = ArboSellerAddressMandatorySchemaBasicRequired;
    const requiredField = useRequiredField(schema);
    // const phoneUtil = PhoneNumberUtil.getInstance();
    // const updatedSchema = schema.shape({
    //     phone: yup
    //         .string()
    //         .phone(selectedCountry?.textCode as CountryCode, t("Phone must be a valid phone number for region ") + selectedCountry?.name)
    //         .test("startsWithPlus", t("The phone number must start with the country code ") + selectedCountry?.code, function (value) {
    //             return (value || "").startsWith(selectedCountry?.code);
    //         })
    //         .test(
    //             "startsWithPlusAndGoogleVerify",
    //             t("Bad phone number format. The phone number must be mobile with international prefix ") + selectedCountry?.code,
    //             function (value) {
    //                 let phoneNumber;
    //                 try {
    //                     phoneNumber = phoneUtil.parseAndKeepRawInput(value, selectedCountry?.textCode);
    //                 } catch (err) {
    //                     return false;
    //                 }
    //                 console.log(phoneUtil.isValidNumber(phoneNumber));
    //                 console.log(phoneUtil.getNumberType(phoneNumber));
    //                 return phoneUtil.isValidNumber(phoneNumber);
    //                 // &&                         (phoneUtil.getNumberType(phoneNumber) === PhoneNumberType.FIXED_LINE || phoneUtil.getNumberType(phoneNumber) === PhoneNumberType.MOBILE)
    //             }
    //         )
    //         .required(),
    // });
    const {
        register,
        handleSubmit,
        setValue,
        formState: {errors},
    } = useForm<CompanyInputs>({
        defaultValues: defaultValues,
        // resolver: yupResolver(updatedSchema),
        resolver: yupResolver(schema),
    });

    const setSelectedCustomerTypeHandler = (customerType: CustomerType) => {
        setSelectedCustomerType(customerType);
    };

    const onSubmitHandler = (data: any) => {
        // if (!data.company_type) {
        //     delete data.cin;
        //     delete data.company_name;
        // }
        //
        // if (emailVerified) {
        //     data.email_verified = true;
        // }
        console.log("onSubmitHandler", data, props.editEnabled);
        if (props.editEnabled) {
            props.onSubmitted(data);
            return;
        }

        if (!packagesCart) {
            toast.error("Packages cart must be created");
        }
        consoleLog("onSubmitHandler", data);
        toast.dismiss();

        // TODO: dispatch with return props.onSubmitted(company)
        if (packagesCart && data.reservation_note) {
            dispatch<ThunkAction<any, any>>(BookingAction.sendReservationNote(packagesCart, data.reservation_note)).then(r => {
                dispatch(BookingAction.pushReservationNote(r.reservationNote));
            });
        }
        return dispatch(BookingAction.fetchSetCustomer(data, packagesCart)).then(r => {
            props.onSubmitted(r.customer);
            return Promise.resolve(true);
        });
    };

    const onSubmitAsLead = (data: any) => {
        consoleLog("onSubmitAsLead", data);

        // if (!data.company_type) {
        //     delete data.cin;
        //     delete data.company_name;
        // }
        //
        // if (emailVerified) {
        //     data.email_verified = true;
        // }
        if (props.editEnabled) {
            props.onSubmitted(data);
            return;
        }

        if (!packagesCart) {
            toast.error("Packages cart must be created");
            return;
        }
        toast.dismiss();

        // TODO: dispatch with return props.onSubmitted(company)
        if (packagesCart && data.reservation_note) {
            dispatch<ThunkAction<any, any>>(BookingAction.sendReservationNote(packagesCart, data.reservation_note)).then(r => {
                dispatch(BookingAction.pushReservationNote(r.reservationNote));
            });
        }
        return dispatch(SellerAction.fetchSetCustomerAsLead(data, packagesCart)).then(r => {
            props.onSubmitted(r.customer);
            return Promise.resolve(true);
        });
    };

    const setSelectedBusinessCategory = (value: any) => {
        setValue("business_category", value);
    };

    const onErrorHandler = (error: any) => {
        console.log("onErrorHandler", error);
    };

    useEffect(() => {
    }, []);

    return (
        <div
            className={clsx(" w-full flex-shrink-1 flex-grow-1 flex flex-col space-y-2", !emailVerified && "grayscale")}>
            {/*<h2 className={"font-medium text-xl"}>{t("New customer or company information")}</h2>*/}

            {!EMAIL_VERIFICATION_REQUIRED && (
                <CardComponent>
                    <div className="col-span-2 sm:col-span-2 space-y-4 mb-2">
                        <CompanyFormInput
                            disabled={!emailVerified}
                            errors={errors}
                            iconName={"email"}
                            label={"Email"}
                            name={"email"}
                            register={register}
                            required={requiredField.testField("email")}
                        />
                    </div>
                </CardComponent>
            )}

            <form className={"flex flex-col space-y-6"} onSubmit={handleSubmit(onSubmitHandler, onErrorHandler)}>
                <input type={"hidden"} {...register("id")} />
                <CardComponent>
                    <div className={"CompanySwitcher col-span-2 flex flex-col justify-between items-center mb-2"}>
                        <div className={"CompanySwitcherInput flex flex-col w-full"}>
                            <CardLabel title={t("Customer information")} />
                            <p className="text-sm leading-5 text-gray-700 hidden">{t("Would you like to register as a business company?")}</p>
                            <fieldset className="my-4">
                                <legend className="sr-only">{t("Company type")}</legend>
                                <div className="space-y-4 sm:flex sm:items-center sm:space-y-0 sm:space-x-10">
                                    {companyTypes.map(companyType => (
                                        <div
                                            key={companyType.id}
                                            className="flex items-center"
                                            onClick={() => setSelectedCustomerTypeHandler(companyType.type)}
                                        >
                                            <input
                                                {...register("company_type")}
                                                checked={companyType.type === selectedCustomerType}
                                                className="checked:bg-cta-800 active:ring-2 active:ring-cta-800 active:ring-offset-2 rounded-full h-4 w-4 text-contrast-800"
                                                // defaultChecked={companyType.type === CustomerType.Private}
                                                disabled={!emailVerified}
                                                id={`company-${companyType.type}`}
                                                type="radio"
                                                value={companyType.type === CustomerType.Business ? "true" : "false"}
                                            />
                                            <label className="ml-3 block text-sm font-medium text-gray-800"
                                                   htmlFor={`company-${companyType.type}`}>
                                                {t(companyType.type)}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </fieldset>
                        </div>
                    </div>
                    {selectedCustomerType === CustomerType.Business && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 col-span-2 sm:col-span-2 mb-2">
                            <CompanyFormInput
                                className={"col-span-1 md:col-span-1"}
                                disabled={!emailVerified}
                                errors={errors}
                                iconName={"company_name"}
                                label={"Company name"}
                                name={"company_name"}
                                register={register}
                                // required={selectedCustomerType === CustomerType.Business}
                            />

                            {DEFAULT_HALL === HallCode.Zwolle && (
                                <CompanyFormInput
                                    className={"col-span-1 md:col-span-1"}
                                    disabled={!emailVerified}
                                    errors={errors}
                                    iconName={"cin"}
                                    label={"Company number ID"}
                                    name={"cin"}
                                    register={register}
                                    required={requiredField.testField("cin")}
                                />
                            )}
                            <CompanyFormDropdown
                                className={"col-span-1 md:col-span-1"}
                                defaultValue={defaultValues?.business_category ? defaultValues.business_category : null}
                                disabled={!emailVerified}
                                errors={errors}
                                label={"Business category"}
                                name={"business_category"}
                                register={register}
                                required={requiredField.testField("business_category")}
                                options={businessCategories}
                                setSelected={setSelectedBusinessCategory}
                            />
                            <CompanyFormInput
                                className={"col-span-2 md:col-span-1"}
                                disabled={!emailVerified}
                                errors={errors}
                                iconName={"street_address"}
                                label={"Street address"}
                                name={"street_address"}
                                register={register}
                                required={requiredField.testField("street_address")}
                            />
                            <CompanyFormInput
                                className={"col-span-2 md:col-span-1"}
                                disabled={!emailVerified}
                                errors={errors}
                                iconName={"postal_code"}
                                label={"Postal code"}
                                name={"postal_code"}
                                register={register}
                                required={requiredField.testField("postal_code")}
                            />
                            <CompanyFormInput
                                className={"col-span-2 md:col-span-1"}
                                disabled={!emailVerified}
                                errors={errors}
                                iconName={"city"}
                                label={"City"}
                                name={"city"}
                                register={register}
                                required={requiredField.testField("city")}
                            />
                            <h3 className={"font-semibold col-span-1 md:col-span-2"}>{t("Contact person")}</h3>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 col-span-2 sm:col-span-2">
                        <CompanyFormInput
                            className={"col-span-2 md:col-span-1"}
                            disabled={!emailVerified}
                            errors={errors}
                            iconName={"first_name"}
                            label={"First name"}
                            name={"first_name"}
                            register={register}
                            required={requiredField.testField("first_name")}
                        />
                        <CompanyFormInput
                            className={"col-span-2 md:col-span-1"}
                            disabled={!emailVerified}
                            errors={errors}
                            iconName={"last_name"}
                            label={"Last name"}
                            name={"last_name"}
                            register={register}
                            required={requiredField.testField("last_name")}
                        />
                        <CompanyFormPhoneWithCountrySelect
                            defaultCountry={selectedCountry?.textCode}
                            defaultValue={defaultValues.phone}
                            disabled={!emailVerified}
                            errors={errors}
                            label={t("Phone")}
                            name={"phone"}
                            options={filteredCountryList([
                                "AT",
                                "BE",
                                "BG",
                                "HR",
                                "CY",
                                "CZ",
                                "DK",
                                "EE",
                                "FI",
                                "FR",
                                "DE",
                                "GR",
                                "HU",
                                "IE",
                                "IT",
                                "LV",
                                "LI",
                                "LT",
                                "LU",
                                "MT",
                                "NL",
                                "PL",
                                "PT",
                                "RO",
                                "SK",
                                "SI",
                                "ES",
                                "SE",
                                "GB",
                                "NO",
                                "SE",
                                "CH",
                                "US",
                                "AD",
                                "ES",
                                "ES",
                                "FR",
                                "FO",
                                "GI",
                                "IM",
                                "AX",
                            ])}
                            register={register}
                            required={requiredField.testField("phone")}
                            setSelectedCountry={setSelectedCountry}
                            setValue={value => setValue("phone", value)}
                        />
                        {DEFAULT_HALL !== HallCode.Zwolle && selectedCustomerType === CustomerType.Private && (
                            <>
                                <CompanyFormInput
                                    disabled={!emailVerified}
                                    errors={errors}
                                    iconName={"street_address"}
                                    label={"Street address"}
                                    name={"street_address"}
                                    register={register}
                                    required={requiredField.testField("street_address")}
                                />
                                <CompanyFormInput
                                    disabled={!emailVerified}
                                    errors={errors}
                                    iconName={"postal_code"}
                                    label={"Postal code"}
                                    name={"postal_code"}
                                    register={register}
                                    required={requiredField.testField("postal_code")}
                                />
                                <CompanyFormInput
                                    disabled={!emailVerified}
                                    errors={errors}
                                    iconName={"city"}
                                    label={"City"}
                                    name={"city"}
                                    register={register}
                                    required={requiredField.testField("city")}
                                />
                            </>
                        )}
                    </div>
                </CardComponent>

                {!hideReservationNotes && (
                    <CardComponent>
                        <div className={"BookingReservationNoteTitle mb-2"}>
                            <h3 className={"font-bold"}>{t("Send us a note")}</h3>
                        </div>
                        <textarea
                            className={"p-2 w-full border-gray-500 border rounded text-base font-medium min-h-[80px]"}
                            {...register("reservation_note")}
                        />
                    </CardComponent>
                )}

                <div className={"flex flex-row gap-4 w-full"}>
                    <button
                        className={
                            "bg-cta-400 hover:bg-cta-800 px-3 py-2 text-white rounded text-base font-bold flex flex-row self-baseline items-center justify-center w-full"
                        }
                        onClick={handleSubmit(onSubmitHandler, onErrorHandler)}
                    >
                        <CheckIcon className={"w-4 h-4 flex-none mr-1.5"} />
                        {props?.company ? t("Update") : t("Create")}
                    </button>

                    {!props?.company && (
                        <button
                            className={
                                "bg-blue-400 hover:bg-blue-500 px-3 py-2 text-white rounded text-base font-bold flex flex-row self-baseline items-center justify-center w-full"
                            }
                            onClick={onSubmitAsLead}
                        >
                            <FireIcon className={"w-4 h-4 flex-none mr-1.5"} />
                            {t("Save as Lead")}
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default SellerCompanyForm;
